// src/pages/Settings/index.js
import React from "react";
import {
  Card,
  Radio,
  Space,
  Typography,
  Divider,
  Switch,
  Input,
  Upload,
  InputNumber,
} from "antd";

const THEME_OPTIONS = [
  {
    value: "aura",
    label: "轻盈蓝粉",
    desc: "清爽蓝粉渐变，轻盈通透",
  },
  {
    value: "latte",
    label: "奶油薄荷",
    desc: "温暖柔和，清甜但不腻",
  },
  {
    value: "tencent",
    label: "蓝绿清爽",
    desc: "蓝绿轻呼吸，清爽科技感",
  },
  {
    value: "fresh",
    label: "清新蓝绿",
    desc: "清爽明亮，轻量无压力",
  },
  {
    value: "business",
    label: "商务深蓝",
    desc: "克制稳重，适合正式内容",
  },
  {
    value: "dark",
    label: "暗夜灰黑",
    desc: "低亮度护眼，层次明确",
  },
  {
    value: "oriental",
    label: "墨韵米白",
    desc: "墨色留白，温润雅致",
  },
  {
    value: "minimal",
    label: "极简黑白",
    desc: "高对比、简洁克制",
  },
  {
    value: "cyberpunk",
    label: "霓虹暗紫",
    desc: "霓虹对比强，未来感拉满",
  },
  {
    value: "sunset",
    label: "橙蓝夜幕",
    desc: "橙黄暖调 + 深蓝冷光 + 黑色基底",
  },
];

export default function Settings({
  themeName,
  onThemeChange,
  layoutColumns,
  onLayoutChange,
  groupByDay,
  onGroupByDayChange,
  bgImageEnabled,
  onBgImageEnabledChange,
  bgImageUrl,
  onBgImageUrlChange,
  bgVideoEnabled,
  onBgVideoEnabledChange,
  bgVideoUrl,
  onBgVideoUrlChange,
  bgFocusEnabled,
  onBgFocusEnabledChange,
  customTheme,
  onCustomThemeChange,
  customThemeDefaults,
  lastNonCustomTheme,
}) {
  const displayVideoUrl = bgVideoUrl.startsWith("blob:") ? "" : bgVideoUrl;
  const activeCustomTheme = customTheme || customThemeDefaults;
  const isCustomActive = themeName === "custom";
  const themeValue = isCustomActive ? lastNonCustomTheme : themeName;
  const isValidHex = (value) => {
    if (typeof value !== "string") return false;
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
  };
  const getSafeHex = (value) => (isValidHex(value) ? value.trim() : "#000000");
  const updateCustomTheme = (key, value) => {
    onCustomThemeChange({ ...activeCustomTheme, [key]: value });
  };
  const resetCustomTheme = () => {
    onCustomThemeChange({ ...customThemeDefaults });
  };

  const paletteGroups = [
    {
      title: "背景与头部",
      items: [
        { key: "appBgStart", label: "背景起色" },
        { key: "appBgEnd", label: "背景终色" },
        { key: "appGlow1", label: "背景高光 1" },
        { key: "appGlow2", label: "背景高光 2" },
        { key: "headerStart", label: "头部起色" },
        { key: "headerEnd", label: "头部终色" },
        { key: "headerBorder", label: "头部分割" },
        { key: "headerText", label: "头部文字" },
        { key: "headerSubtleText", label: "头部次文字" },
      ],
    },
    {
      title: "卡片与阴影",
      items: [
        { key: "cardBg", label: "卡片背景" },
        { key: "cardBodyBg", label: "卡片内容" },
        { key: "cardBorder", label: "卡片边框" },
        { key: "shadowColor", label: "阴影颜色" },
      ],
    },
    {
      title: "文字与强调",
      items: [
        { key: "textPrimary", label: "主文字" },
        { key: "textSecondary", label: "次文字" },
        { key: "accentStart", label: "强调起色" },
        { key: "accentEnd", label: "强调终色" },
      ],
    },
    {
      title: "标签与输入框",
      items: [
        { key: "tagBg", label: "标签背景" },
        { key: "tagBorder", label: "标签边框" },
        { key: "tagColor", label: "标签文字" },
        { key: "inputBg", label: "输入背景" },
      ],
    },
    {
      title: "时钟与页底",
      items: [
        { key: "clockBg", label: "时钟背景" },
        { key: "clockBorder", label: "时钟边框" },
        { key: "clockText", label: "时钟文字" },
        { key: "footerBg", label: "页底背景" },
        { key: "footerText", label: "页底文字" },
        { key: "footerBorder", label: "页底边框" },
      ],
    },
    {
      title: "滚动条",
      items: [
        { key: "scrollbarTrack", label: "轨道" },
        { key: "scrollbarThumb", label: "滑块" },
        { key: "scrollbarThumbHover", label: "滑块悬停" },
      ],
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 18,
        border: "1px solid var(--card-border)",
        background: "var(--card-bg)",
        boxShadow: "var(--card-shadow)",
        backdropFilter: "var(--card-blur)",
      }}
      bodyStyle={{ padding: "22px 26px" }}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Typography.Title
            level={4}
            style={{ marginBottom: 4, color: "var(--text-primary)" }}
          >
            主题设置
          </Typography.Title>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            选择你喜欢的视觉风格，立即生效
          </Typography.Text>
        </div>

        <Radio.Group
          value={themeValue}
          onChange={(e) => onThemeChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          {THEME_OPTIONS.map((opt) => (
            <Radio.Button key={opt.value} value={opt.value}>
              {opt.label}
            </Radio.Button>
          ))}
        </Radio.Group>

        <Space direction="vertical" size={6}>
          {THEME_OPTIONS.map((opt) => (
            <Typography.Text
              key={opt.value}
              style={{
                color: opt.value === themeName ? "var(--header-text)" : "var(--text-secondary)",
              }}
            >
              {opt.label}：{opt.desc}
            </Typography.Text>
          ))}
        </Space>

        <Divider style={{ margin: "8px 0", borderColor: "rgba(0,0,0,0.06)" }} />

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            自定义主题
          </Typography.Text>
          <Space
            direction="vertical"
            size={10}
            style={{ width: "100%", marginTop: 8 }}
          >
            <Space align="center" wrap>
              <Switch
                checked={isCustomActive}
                onChange={(enabled) => {
                  if (enabled) {
                    onThemeChange("custom");
                    return;
                  }
                  onThemeChange(lastNonCustomTheme || "aura");
                }}
              />
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                启用自定义主题
              </Typography.Text>
              <Divider type="vertical" />
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                {isCustomActive ? "正在使用自定义主题" : "切换到自定义主题后生效"}
              </Typography.Text>
              <Divider type="vertical" />
              <Space size={8}>
                <span
                  onClick={resetCustomTheme}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") resetCustomTheme();
                  }}
                  style={{
                    cursor: "pointer",
                    color: "var(--tag-color)",
                    fontSize: 12,
                  }}
                >
                  重置为默认
                </span>
                <span
                  onClick={() => updateCustomTheme("isDark", !activeCustomTheme?.isDark)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      updateCustomTheme("isDark", !activeCustomTheme?.isDark);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    color: "var(--tag-color)",
                    fontSize: 12,
                  }}
                >
                  {activeCustomTheme?.isDark ? "切换为浅色基调" : "切换为暗色基调"}
                </span>
              </Space>
            </Space>

            {isCustomActive
              ? paletteGroups.map((group) => (
                  <div key={group.title} style={{ width: "100%" }}>
                    <Typography.Text
                      style={{ color: "var(--text-secondary)", fontSize: 12 }}
                    >
                      {group.title}
                    </Typography.Text>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 10,
                        marginTop: 8,
                      }}
                    >
                      {group.items.map((item) => {
                        const value = activeCustomTheme?.[item.key] || "#000000";
                        return (
                          <div
                            key={item.key}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "8px 10px",
                              border: "1px solid var(--card-border)",
                              borderRadius: 10,
                              background: "var(--card-body-bg)",
                            }}
                          >
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                background: value,
                                border: "1px solid var(--card-border)",
                              }}
                            />
                            <Typography.Text style={{ flex: 1, fontSize: 12 }}>
                              {item.label}
                            </Typography.Text>
                            <input
                              type="color"
                              value={getSafeHex(value)}
                              onChange={(e) =>
                                updateCustomTheme(item.key, e.target.value)
                              }
                              aria-label={item.label}
                              style={{
                                width: 28,
                                height: 28,
                                padding: 0,
                                border: "none",
                                background: "transparent",
                              }}
                            />
                            <Input
                              size="small"
                              value={value}
                              onChange={(e) =>
                                updateCustomTheme(item.key, e.target.value)
                              }
                              style={{ width: 96 }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              : null}
          </Space>
        </div>

        <Divider style={{ margin: "8px 0", borderColor: "rgba(0,0,0,0.06)" }} />

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            首页文章布局
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Space align="center">
              <InputNumber
                min={1}
                max={6}
                value={layoutColumns}
                onChange={(value) => {
                  const next = Number(value);
                  onLayoutChange(Number.isFinite(next) ? next : 1);
                }}
                style={{ width: 120 }}
              />
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                一行 {layoutColumns || 1} 个
              </Typography.Text>
            </Space>
          </div>
        </div>

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            复杂分布模式
          </Typography.Text>
          <Space align="center" style={{ marginTop: 8 }}>
            <Switch checked={groupByDay} onChange={onGroupByDayChange} />
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              按天分组（类似手机相册）
            </Typography.Text>
          </Space>
        </div>

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            自定义背景图
          </Typography.Text>
          <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 8 }}>
            <Space align="center">
              <Switch checked={bgImageEnabled} onChange={onBgImageEnabledChange} />
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                启用背景图
              </Typography.Text>
            </Space>
            <Input
              placeholder="输入图片链接，例如：https://example.com/bg.jpg"
              value={bgImageUrl}
              onChange={(e) => onBgImageUrlChange(e.target.value)}
              disabled={!bgImageEnabled}
            />
          </Space>
        </div>

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            背景视频
          </Typography.Text>
          <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 8 }}>
            <Space align="center">
              <Switch checked={bgVideoEnabled} onChange={onBgVideoEnabledChange} />
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                启用视频背景
              </Typography.Text>
            </Space>
            <Upload.Dragger
              accept="video/*"
              multiple={false}
              showUploadList={false}
              disabled={!bgVideoEnabled}
              beforeUpload={(file) => {
                if (!bgVideoEnabled) return false;
                const nextUrl = URL.createObjectURL(file);
                onBgVideoUrlChange(nextUrl);
                return false;
              }}
            >
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                拖拽本地视频到这里（仅本次生效）
              </Typography.Text>
            </Upload.Dragger>
            <Input
              placeholder="持久路径示例：/bg.mp4"
              value={displayVideoUrl}
              onChange={(e) => onBgVideoUrlChange(e.target.value)}
              disabled={!bgVideoEnabled}
            />
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              建议将视频放在 public 目录，再填入路径以便刷新后保留
            </Typography.Text>
          </Space>
        </div>

        <div>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            背景优先效果
          </Typography.Text>
          <Space align="center" style={{ marginTop: 8 }}>
            <Switch checked={bgFocusEnabled} onChange={onBgFocusEnabledChange} />
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              点击空白背景时临时取消毛玻璃
            </Typography.Text>
          </Space>
        </div>
      </Space>
    </Card>
  );
}
